import { AppBar, Toolbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { color } from '../../app/theme';
import { ModalType } from '../../app/types';
import defaultImageUrl from '../../assets/images/svg/img_avatar_org.svg';
import logo from '../../assets/next/logo/mh-logo.png';
import {
  checkPoints,
  getDatasetInitCount,
  publishData,
  saveData,
} from '../../features/aiBotSlice';
import { closeModal, openModal } from '../../features/modalSlice';
import Badge from '../shared/Badge';
import Button from '../shared/Button';

const useStyles = makeStyles(
  () => ({
    root: {
      position: 'fixed',
      top: 0,
      color: color.$3,
      height: 80,
      backgroundColor: color.$7,
      boxShadow: 'unset',
      borderBottom: `1px solid ${color.$9}`,
    },
    toolbar: {
      height: 'inherit',
    },
    title: {
      flex: '1 1 auto',
      fontSize: 20,
      margin: '0 0 0 12px',
      display: 'flex',
      alignItems: 'center',
    },
    button: {
      marginRight: 8,
      marginLeft: 4,
      '& > span': {
        width: 12,
        height: 12,
        top: '6px',
        right: '6px',
        borderRadius: '12px',
      },
    },
    avatar: {
      display: 'flex',
      alignItems: 'flex-end',
    },
    left: {
      display: 'flex',
      alignItems: 'center',
    },
    image: {
      marginLeft: 24,
      borderRadius: 28,
    },

    onlyView: {
      padding: '5px 8px',
      color: color.$12,
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      marginLeft: 8,
      backgroundColor: color.$8,
      borderRadius: 4,
    },
    editer: {
      padding: '5px 8px',
      color: color.$2,
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      marginLeft: 8,
      backgroundColor: hexToRgba(color.$2, 10),
      borderRadius: 4,
    },
    text: {
      fontSize: 12,
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 1,
      color: color.$10,
      marginRight: 8,
      textAlign: 'right',
    },
    draft: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$17,
      marginBottom: 8,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      '& > div': {
        color: color.$2,
        textDecoration: 'underline',
        cursor: 'pointer',
      },
    },
  }),
  {
    name: 'Header',
  },
);

let image = null;

function Header({ pathKey }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [imageSrc, setImageSrc] = useState(null);
  const { isLoading, isActive, data, isSaveDraft } = useSelector(
    store => store.aiBot,
  );
  const { icon } = useSelector(store => store.organization);
  const { orgId, id } = useParams();
  const { updatedAt, isDraft } = data || {};

  const onSave = () => {
    dispatch(saveData(orgId, id));
  };

  const onPublish = async () => {
    const response = await dispatch(getDatasetInitCount(orgId, id));

    if (response?.web || response?.file) {
      dispatch(
        openModal({
          modalType: ModalType.ALERT_MODAL,
          modalProps: {
            title: t('aiBot:error/dataset_should_embedding.title'),
            content: (
              <>
                <div className={classes.row}>
                  {t('aiBot:error/dataset_should_embedding.content')}
                  <div
                    onClick={() => {
                      history.push(`/${pathKey}/${orgId}/${id}/knowledge`);
                      dispatch(closeModal());
                    }}
                  >
                    {t('aiBot:error/dataset_should_embedding.content1')}
                  </div>
                  {t('aiBot:error/dataset_should_embedding.content2')}
                </div>
                <br />
                <div>{t('aiBot:error/dataset_should_embedding.content3')}</div>
                <div>{t('aiBot:error/dataset_should_embedding.content4')}</div>
              </>
            ),
            onCancel: () => {
              dispatch(closeModal());
            },
            onClick: async () => {
              await dispatch(publishData(orgId, id, true));
              await dispatch(checkPoints(orgId));
              dispatch(closeModal());
            },
            cancelText: t('modal.not-now'),
            submitText: t('modal.publish'),
          },
        }),
      );
    } else {
      dispatch(
        openModal({
          modalType: ModalType.ALERT_MODAL,
          modalProps: {
            title: t(`${pathKey}:publish-modal-title`),
            content: (
              <div>
                <div>{t(`${pathKey}:publish-modal-content`)}</div>
                <br />
                <div>{t(`${pathKey}:publish-modal-content2`)}</div>
              </div>
            ),
            onCancel: () => {
              dispatch(closeModal());
            },
            onClick: async () => {
              await dispatch(publishData(orgId, id));
              await dispatch(checkPoints(orgId));
              dispatch(closeModal());
            },
            cancelText: t('modal.not-now'),
            submitText: t('modal.publish'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (icon && imageSrc !== icon) {
      image = new Image();
      image.onload = () => {
        setImageSrc(image.currentSrc);
      };
      image.src = icon;
    }

    return () => {
      if (image) {
        image.onload = () => {};
      }
    };
  }, [icon, imageSrc]);

  return (
    <AppBar className={classes.root} position="static">
      <Toolbar className={classes.toolbar}>
        <img src={logo} alt="logo" width="32" height="32" />
        <div className={classes.title}>
          {t(`${pathKey}:title`)}
          <div
            className={classnames({
              [classes.onlyView]: isActive,
              [classes.editer]: !isActive,
            })}
          >
            {isActive ? t('only-view') : t('can-editer')}
          </div>
        </div>
        <div className={classes.avatar}>
          <div className={classes.text}>
            <div className={classes.draft}>
              {isSaveDraft || isDraft
                ? isSaveDraft
                  ? t(`${pathKey}:isSaveDraft`)
                  : t(`${pathKey}:isDraft`)
                : ''}
            </div>
            {updatedAt && (
              <div>
                {t('updatedAt', {
                  date: moment(updatedAt).format('YYYY-MM-DD HH:mm'),
                })}
              </div>
            )}
          </div>
          <div className={classes.left}>
            <Badge
              className={classes.button}
              badgeContent=""
              badgeColor="primary"
              invisible={!isSaveDraft}
            >
              <Button
                topic="primaryGreen"
                onClick={onSave}
                isLoading={isLoading}
              >
                {t(`${pathKey}:save`)}
              </Button>
            </Badge>
            <Badge
              className={classes.button}
              badgeContent=""
              badgeColor="primary"
              invisible={!(isSaveDraft || isDraft)}
            >
              <Button
                topic="primaryGreen"
                onClick={onPublish}
                isLoading={isLoading}
                // isDisable={isActive ? !isDraft : false}
              >
                {t(`${pathKey}:publish`)}
              </Button>
            </Badge>
            <img
              className={classes.image}
              src={imageSrc || defaultImageUrl}
              alt="org-img"
              width={40}
              height={40}
            />
          </div>
        </div>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
